import React, { useState, useEffect } from 'react';
import axios from '../../../services/api';
import { FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CreateContractModal from './CreateContractModal';
import DeleteConfirmModal from '../../../components/modals/ConfirmModal';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const ContractModal = ({ isOpen, onClose, community, onSuccess }) => {
  const { t } = useTranslation();
  const [contracts, setContracts] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && community) {
      fetchContracts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, community]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`/contracts/${community._id}`);
      setContracts(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = (contract) => {
    setSelectedContract(contract);
    setIsCreateModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/contracts/${selectedContract._id}`);
      toast.success(t('Delete Successfully'));
      setIsDeleteModalOpen(false);
      setSelectedContract(null);
      fetchContracts();
      onSuccess();
    } catch (error) {
      console.error(error);
    }
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[10%] pt-[5%] z-50">
      <div className="bg-white w-[60vw] h-[80vh] rounded-lg shadow-xl flex flex-col">
        <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
          <h2 className="text-lg font-semibold">{t('Contract')}</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <FaTimes />
          </button>
        </div>

        <div className={`${contracts.length > 0 ? 'overflow-auto' : 'overflow-hidden'} flex-1 mt-2 relative px-2`}>
          <table className="w-full">
            <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
              <tr className="border-b border-skin-base/10">
                <th className="sticky left-0 z-10 bg-white px-4 py-2 text-left font-semibold text-skin-base min-w-[60px]">
                  {t('No.')}
                </th>
                <th className="px-4 py-2 text-left font-semibold text-skin-base min-w-[150px]">
                  {t('Contract ID')}
                </th>
                <th className="px-4 py-2 text-left font-semibold text-skin-base min-w-[120px]">
                  {t('Start Time')}
                </th>
                <th className="px-4 py-2 text-left font-semibold text-skin-base min-w-[120px]">
                  {t('End Time')}
                </th>
                <th className="px-4 py-2 text-left font-semibold text-skin-base min-w-[120px]">
                  {t('Amount')}
                </th>
                <th className="px-4 py-2 text-left font-semibold text-skin-base min-w-[100px]">
                  {t('Due')}
                </th>
                <th className="sticky right-0 z-10 bg-white px-4 py-2 text-left font-semibold text-skin-base min-w-[100px] text-right">
                  {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((contract, index) => (
                <tr key={contract._id}>
                  <td className="sticky left-0 z-10 bg-white px-4 py-2 whitespace-nowrap border-b border-gray-200">
                    {index + 1}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{contract.contractId}</td>
                  <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">
                    {new Date(contract.startDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">
                    {new Date(contract.endDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{contract.paymentAmount}</td>
                  <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">
                    {moment(contract.endDate).diff(moment(contract.startDate), 'days')} days
                  </td>
                  <td className="sticky right-0 z-10 bg-white px-4 py-2 whitespace-nowrap border-b border-gray-200">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => handleEdit(contract)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedContract(contract);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={() => {
              setSelectedContract(null);
              setIsCreateModalOpen(true);
            }}
            className="px-4 py-1.5 border border-gray-300 rounded hover:bg-gray-50 bg-blue-500 text-white hover:bg-blue-600"
          >
            {t('Create New')}
          </button>
        </div>

        <CreateContractModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setSelectedContract(null);
          }}
          community={community}
          contract={selectedContract}
          onSuccess={()=>{
            fetchContracts();
            onSuccess();
          }}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedContract(null);
          }}
          onConfirm={handleDelete}
          title={t('Delete Contract')}
          message={t('Are you sure you want to delete this contract? This action cannot be undone.')}
          confirmText={t('Delete')}
          cancelText={t('Cancel')}
          type="danger"
        />
      </div>
    </div>
  );
};

export default ContractModal;
